import React, { Component, FunctionComponent } from 'react';
import styled, { css } from '@benzinga/themetron';

import { Bar, BarChart as _BarChart, Fill, Label, Title } from './BarChart';
import { BarProps } from './BarChart/BarChart';

const BarChart = styled(_BarChart)`
  flex-basis: 225px;
  flex-grow: 1;
  flex-shrink: 0;
  margin: 0.25em 0.5em;
`;

interface ChartFillProps {
  positive?: boolean;
}

const ChartFill = styled(Fill)<ChartFillProps>`
  background-color: ${props =>
    props.positive ? props.theme.colors.statistic.positive : props.theme.colors.statistic.negative};
`;

interface ChartLabelProps {
  alignBottom?: boolean;
  alignLeft?: boolean;
  alignRight?: boolean;
  centered?: boolean;
  direction?: 'left' | 'right';
  flipped?: boolean;
  hasTriangle?: boolean;
  positive?: boolean;
}

const ChartLabel = styled(Label)<ChartLabelProps>`
  ${({ alignBottom, alignLeft, alignRight, centered = true, direction, flipped = false, hasTriangle, theme }) => {
    let triangle = css`
      border-bottom: 5px solid ${theme.colors.backgroundActive};
      content: '';
      display: block;
      height: 0;
      position: absolute;
      top: -5px;
      width: 0;
    `;

    // the direction is the way the label normally would face. Only takes effect if
    // not center aligned
    //
    // if the direction is left, the triangle should be on the right side of the label

    if (direction === 'left' || (direction === 'right' && flipped)) {
      triangle = css`
        ${triangle};
        border-right: 5px solid transparent;
        left: 0;
      `;
    } else if (direction === 'right' || (direction === 'left' && flipped)) {
      triangle = css`
        ${triangle};
        border-left: 5px solid transparent;
        right: 0;
      `;
    } else {
      triangle = css`
        ${triangle};
        border-right: 5px solid transparent;
        border-left: 5px solid transparent;
        right: calc(50% - 5px);
      `;
    }

    let styles = css`
      background-color: ${({ theme }) => theme.colors.backgroundActive};
      font-size: 0.9em;
      font-weight: 400;
      height: 16px;
      line-height: 1em;
      padding: 0.15em 0.25em;
      vertical-align: middle;
      z-index: 1;
    `;

    // This will put the labels underneath the bar, rather than at the bottom of the bar
    if (alignBottom) {
      if (centered && !(alignLeft || alignRight)) {
        styles = css`
          ${styles};
          transform: ${hasTriangle ? `translate(-50%, calc(100% + 5px))` : `translate(-50%, 100%)`};
          left: 50%;
        `;
      } else {
        styles = css`
          ${styles};
          transform: ${hasTriangle ? `translateY(calc(100% + 5px))` : `translateY(100%)`};
        `;
      }
    }

    if (hasTriangle) {
      styles = css`
        ${styles};
        &:after {
          ${triangle};
        }
      `;
    }

    return styles;
  }}
`;

const ChartBar = styled(Bar)<BarProps>`
  background-color: ${({ theme }) => theme.colors.backgroundInactive};
  box-shadow: 0px 0px 4px 0px ${({ theme }) => theme.colors.backgroundActive};
  margin-bottom: 30px;
  margin-top: 5px;
  min-width: 2px;
`;

const positionThreshold = 0.25;
const widthPercentageThreshold = 0.2;

const range = (a: number, b: number): number => Math.abs(a - b);

const percentile = (a: number, b: number, range: number): number => (a - b) / range;

interface FiftyTwoWeekBarChartProps {
  dayHigh?: number;
  dayLow?: number;
  fiftyTwoWeekHigh?: number;
  fiftyTwoWeekLow?: number;
}

const FiftyTwoWeekBarChart: FunctionComponent<FiftyTwoWeekBarChartProps> = ({
  dayHigh,
  dayLow,
  fiftyTwoWeekHigh,
  fiftyTwoWeekLow,
}) => {
  if (!dayHigh || !dayLow || !fiftyTwoWeekHigh || !fiftyTwoWeekLow) {
    return null;
  }

  const leftEdge = Math.min(fiftyTwoWeekLow, dayLow);
  const rightEdge = Math.max(fiftyTwoWeekHigh, dayHigh);
  const totalRange = range(leftEdge, rightEdge);

  const positive = (dayHigh + dayLow) / 2 >= (fiftyTwoWeekHigh + fiftyTwoWeekLow) / 2;

  const start = percentile(dayLow, leftEdge, totalRange);
  const end = percentile(dayHigh, leftEdge, totalRange);

  return (
    <BarChart>
      <Title>
        <Label alignLeft>{leftEdge}</Label>
        52 Week Low/High
        <Label alignRight>{rightEdge}</Label>
      </Title>
      <ChartBar height={6}>
        <ChartFill end={end} positive={positive} start={start}>
          <ChartLabel alignBottom centered hasTriangle>
            Session Range
          </ChartLabel>
        </ChartFill>
      </ChartBar>
    </BarChart>
  );
};

FiftyTwoWeekBarChart.displayName = 'FiftyTwoWeekBarChart';

interface SessionRangeBarChartProps {
  dayHigh?: number;
  dayLow?: number;
  open?: number;
  previousClose?: number;
  price?: string;
}

const SessionRangeBarChart: FunctionComponent<SessionRangeBarChartProps> = props => {
  const { dayHigh, dayLow, open, previousClose } = props;

  if (!dayHigh || !dayLow || !previousClose || !open || !props.price) {
    return null;
  }

  const price = Number.parseFloat(props.price);
  if (!price || isNaN(dayHigh) || isNaN(dayLow) || isNaN(previousClose) || isNaN(open)) {
    return null;
  }

  let constantEdge = previousClose;
  let useOpen = false;

  if (open) {
    useOpen = true;
    constantEdge = open;
  }

  const low = Math.min(price, dayLow, constantEdge);
  const high = Math.max(price, constantEdge, dayHigh);

  let lowestBarValue;
  let highestBarValue;
  let positive = false;

  if (constantEdge < price) {
    positive = true;
  }

  if (positive) {
    highestBarValue = price;
    lowestBarValue = constantEdge;
  } else {
    highestBarValue = constantEdge;
    lowestBarValue = price;
  }

  let label;

  const totalRange = range(high, low);

  const width = percentile(highestBarValue, lowestBarValue, totalRange);

  const priceLabel = `Price: ${price}`;
  const openLabel = `Open: ${open}`;
  const previousCloseLabel = `Previous Close: ${previousClose}`;

  let leftLabel;
  let rightLabel;
  // Tweak width to avoid label overlap
  if (width < widthPercentageThreshold) {
    label = `Price: ${price}`;
  } else {
    if (!positive) {
      leftLabel = priceLabel;
      rightLabel = useOpen ? openLabel : previousCloseLabel;
    } else {
      leftLabel = useOpen ? openLabel : previousCloseLabel;
      rightLabel = priceLabel;
    }
  }

  // bar is start / end
  // low / high is range
  const start = percentile(lowestBarValue, low, totalRange);
  const end = percentile(highestBarValue, low, totalRange);

  return (
    <BarChart>
      <Title>
        <Label alignLeft>{low}</Label>
        Session Low/High
        <Label alignRight>{high}</Label>
      </Title>
      <ChartBar height={6}>
        <ChartFill end={end} start={start}>
          {leftLabel && (
            <ChartLabel alignBottom alignLeft direction="right" flipped={start < positionThreshold} hasTriangle>
              {leftLabel}
            </ChartLabel>
          )}
          {rightLabel && (
            <ChartLabel alignBottom alignRight direction="left" flipped={1 - end < positionThreshold} hasTriangle>
              {rightLabel}
            </ChartLabel>
          )}
          {label && (
            <ChartLabel alignBottom centered hasTriangle>
              {label}
            </ChartLabel>
          )}
        </ChartFill>
      </ChartBar>
    </BarChart>
  );
};

SessionRangeBarChart.displayName = 'SessionRangeBarChart';

export interface SymbolDetailsHeaderChartsProps {
  quote: {
    dayHigh?: number;
    dayLow?: number;
    fiftyTwoWeekHigh?: number;
    fiftyTwoWeekLow?: number;
    open?: number;
    previousClose?: number;
    price?: string;
  };
}

const Charts = styled.div({
  display: 'flex',
  flexWrap: 'wrap',
});

export default class SymbolDetailsHeaderCharts extends Component<SymbolDetailsHeaderChartsProps> {
  render() {
    const {
      quote: { dayHigh, dayLow, fiftyTwoWeekHigh, fiftyTwoWeekLow, open, previousClose, price },
    } = this.props;

    return (
      <Charts>
        <FiftyTwoWeekBarChart
          dayHigh={dayHigh}
          dayLow={dayLow}
          fiftyTwoWeekHigh={fiftyTwoWeekHigh}
          fiftyTwoWeekLow={fiftyTwoWeekLow}
        />
        <SessionRangeBarChart
          dayHigh={dayHigh}
          dayLow={dayLow}
          open={open}
          previousClose={previousClose}
          price={price}
        />
      </Charts>
    );
  }
}
